import {
  Component, ElementRef, EventEmitter, Input, Output, ViewChild,
  OnChanges, SimpleChanges
} from '@angular/core';
import { ZoomRound } from '../../../interfaces/zoom-quest.interface';

interface Sparkle { x: number; y: number; r: number; delay: number; }

/**
 * Zoom Quest play surface — a 10-segment number line the child dives into.
 *
 * A magnifier marker snaps to the ticks of the current window. ZOOM IN dives
 * into the segment the marker sits on (window ×÷10, one place value deeper);
 * ZOOM OUT pops back up. At the finest level (step 1) the marker snaps to
 * every unit and PLANT digs for treasure — landing on the target wins.
 *
 * When the current window does not bracket the target, an off-screen arrow
 * pulses left/right ("smaller"/"bigger") — number sense, not the answer.
 *
 * All positions are integers so zoom/snapping is exact. Presentation-only:
 * ZOOM / PLANT are triggered by the shell via @ViewChild; the outcome is
 * reported with `solved` / `missed`.
 */
@Component({
  selector: 'app-zoom-quest-stage',
  templateUrl: './zoom-quest-stage.component.html',
  styleUrls: ['./zoom-quest-stage.component.scss']
})
export class ZoomQuestStageComponent implements OnChanges {
  @Input() round: ZoomRound | null = null;
  @Input() roundKey = 0;
  @Input() interactive = true;
  @Input() currLang: any = 'en';

  @Output() solved = new EventEmitter<void>();
  @Output() missed = new EventEmitter<void>();
  /** Emitted whenever the window changes so the shell can refresh its banner. */
  @Output() zoomChanged = new EventEmitter<void>();

  @ViewChild('svg', { static: true }) svgRef!: ElementRef<SVGSVGElement>;

  readonly VIEW_W = 1000;
  readonly VIEW_H = 520;
  readonly X0 = 80;
  readonly X_END = 920;
  readonly LINE_Y = 380;
  readonly SEGMENTS = 10;

  /** City-skyline silhouette (presentation only) — buildings rise from the
   *  line; scaled/faded per zoom tier to give an atmospheric sense of depth. */
  readonly SKYLINE: { x: number; w: number; h: number }[] = [
    { x: 14, w: 46, h: 118 }, { x: 68, w: 34, h: 182 }, { x: 110, w: 54, h: 96 },
    { x: 172, w: 40, h: 150 }, { x: 220, w: 32, h: 214 }, { x: 260, w: 50, h: 128 },
    { x: 318, w: 38, h: 172 }, { x: 364, w: 58, h: 100 }, { x: 430, w: 34, h: 196 },
    { x: 472, w: 46, h: 138 }, { x: 526, w: 32, h: 232 }, { x: 566, w: 52, h: 110 },
    { x: 626, w: 38, h: 166 }, { x: 672, w: 46, h: 204 }, { x: 726, w: 34, h: 124 },
    { x: 768, w: 54, h: 178 }, { x: 830, w: 40, h: 146 }, { x: 878, w: 44, h: 210 },
    { x: 930, w: 40, h: 118 }
  ];
  /** A few window rows to pick out on the tallest towers (front layer only). */
  readonly WINDOW_ROWS = [0.18, 0.34, 0.5, 0.66, 0.82];

  /** Current window. */
  lo = 0;
  step = 1;
  levelIndex = 0;
  /** Marker value (a multiple of `step` inside [lo, hi]). */
  k = 0;

  solvedState = false;
  wrongFlash = false;
  sparkles: Sparkle[] = [];

  private dragging = false;
  private timers: any[] = [];
  /** Stack of prior windows for ZOOM OUT: {lo, k}. */
  private stack: { lo: number; k: number }[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['round'] || changes['roundKey']) this.reset();
  }

  private reset(): void {
    this.clearTimers();
    this.solvedState = false;
    this.wrongFlash = false;
    this.dragging = false;
    this.sparkles = [];
    this.stack = [];
    this.levelIndex = 0;
    this.lo = this.round?.rootLo ?? 0;
    this.step = this.round?.rootStep ?? 1;
    this.k = this.lo;
    this.zoomChanged.emit();
  }

  // ── Geometry ───────────────────────────────────────────────────────

  get hi(): number { return this.lo + this.SEGMENTS * this.step; }
  private get span(): number { return this.X_END - this.X0; }
  private get segPx(): number { return this.span / this.SEGMENTS; }

  valToX(v: number): number { return this.X0 + ((v - this.lo) / this.step) * this.segPx; }
  get markerX(): number { return this.valToX(this.k); }

  /** The 11 tick values of the current window. */
  get ticks(): number[] {
    return Array.from({ length: this.SEGMENTS + 1 }, (_, i) => this.lo + i * this.step);
  }

  // ── Labels ─────────────────────────────────────────────────────────

  fmt(v: number): string { return v.toLocaleString('en-US'); }
  get markerLabel(): string { return this.fmt(this.k); }

  /** How deep the dive is, as a scenery tier 0..3 (bigger = more zoomed). */
  get sceneTier(): number {
    const total = this.round?.digits ?? 1;
    if (total <= 1) return 3;
    return Math.min(3, Math.round((this.levelIndex / (total - 1)) * 3));
  }

  // ── Off-screen direction hint (magnitude sense) ─────────────────────

  get target(): number { return this.round?.target ?? 0; }
  /** 'left' if the target is smaller than the view, 'right' if bigger. */
  get hintDir(): 'left' | 'right' | null {
    if (!this.round || this.solvedState) return null;
    if (this.target < this.lo) return 'left';
    if (this.target > this.hi) return 'right';
    return null;
  }

  // ── Zoom navigation (called by the shell) ──────────────────────────

  get atFinest(): boolean { return this.levelIndex >= (this.round?.digits ?? 1) - 1; }
  /** Can dive: not finest, marker on a segment start (not the far edge). */
  get canZoomIn(): boolean {
    return this.interactive && !this.solvedState && !this.atFinest && this.k < this.hi;
  }
  get canZoomOut(): boolean {
    return this.interactive && !this.solvedState && this.levelIndex > 0;
  }
  get canPlant(): boolean {
    return this.interactive && !this.solvedState && this.atFinest;
  }

  zoomIn(): void {
    if (!this.canZoomIn) return;
    this.stack.push({ lo: this.lo, k: this.k });
    this.lo = this.k;               // dive into the segment under the marker
    this.step = this.step / 10;
    this.levelIndex++;
    this.k = this.lo;               // land on the new window's left edge
    this.wrongFlash = false;
    this.zoomChanged.emit();
  }

  zoomOut(): void {
    if (!this.canZoomOut) return;
    const prev = this.stack.pop();
    if (!prev) return;
    this.lo = prev.lo;
    this.step = this.step * 10;
    this.levelIndex--;
    this.k = prev.k;
    this.wrongFlash = false;
    this.zoomChanged.emit();
  }

  plant(): void {
    if (!this.canPlant) return;
    if (this.k === this.target) {
      this.solvedState = true;
      this.spawnSparkles(this.markerX);
      this.solved.emit();
    } else {
      this.wrongFlash = true;
      this.timers.push(setTimeout(() => { this.wrongFlash = false; }, 620));
      this.missed.emit();
    }
  }

  // ── Drag ───────────────────────────────────────────────────────────

  onDown(evt: PointerEvent): void {
    if (!this.interactive || this.solvedState) return;
    evt.preventDefault();
    this.dragging = true;
    this.updateFromEvent(evt);
    try { this.svgRef.nativeElement.setPointerCapture(evt.pointerId); } catch { /* older TVs */ }
  }

  onMove(evt: PointerEvent): void {
    if (!this.dragging) return;
    this.updateFromEvent(evt);
  }

  onUp(evt: PointerEvent): void {
    if (!this.dragging) return;
    this.dragging = false;
    try { this.svgRef.nativeElement.releasePointerCapture(evt.pointerId); } catch { /* ignore */ }
  }

  private updateFromEvent(evt: PointerEvent): void {
    const x = this.toSvgX(evt);
    const seg = Math.round((x - this.X0) / this.segPx);
    const clamped = Math.max(0, Math.min(this.SEGMENTS, seg));
    this.k = this.lo + clamped * this.step;
  }

  private spawnSparkles(cx: number): void {
    this.sparkles = Array.from({ length: 12 }, (_, i) => {
      const a = (i / 12) * Math.PI * 2;
      const dist = 26 + (i % 3) * 18;
      return { x: cx + Math.cos(a) * dist, y: this.LINE_Y + Math.sin(a) * dist, r: 4 + (i % 3) * 3, delay: (i % 5) * 35 };
    });
  }

  private toSvgX(evt: PointerEvent): number {
    const svg = this.svgRef.nativeElement;
    const ctm = svg.getScreenCTM();
    if (!ctm) return 0;
    const pt = svg.createSVGPoint();
    pt.x = evt.clientX;
    pt.y = evt.clientY;
    return pt.matrixTransform(ctm.inverse()).x;
  }

  private clearTimers(): void {
    this.timers.forEach(t => clearTimeout(t));
    this.timers = [];
  }
}
